<template>
    <section class="section-card">
        <div class="section-card-header d-flex justify-content-between align-items-center">
            <h4 class="mb-0">Bill</h4>
            <span v-if="existingBillId" class="badge bg-light-primary">Editing Bill #{{ billFormData.bill_number }}</span>
        </div>
        <div class="section-card-body">
            <div class="row">
                <div class="col-md-12 mb-2">
                    <AsyncSelect
                        placeholder="Select product"
                        v-model="billProductId"
                        :api-service="fetchProductList"
                        :reduce="name => name.id"
                        :format-label="option => option.text"
                        :additional-query="{ product_type: 'services' }"
                        label="text"
                    />
                </div>
            </div>

            <div class="mb-2">
                <BillItemList :items="billItems" @onDeleteItem="onDeleteBillItem"/>
            </div>

            <BillTotals :items="billItems"/>

            <div class="row">
                <div class="col-12 mb-1">
                    <label class="form-label" for="billDescription">Ledger Memo</label>
                    <textarea
                        id="billDescription"
                        v-model="billFormData.description"
                        class="form-control"
                        rows="2"
                    ></textarea>
                </div>
                <div class="col-12 mt-auto mb-1">
                    <div class="d-flex flex-wrap gap-1 justify-content-end">
                        <button type="button" class="btn btn-primary" :disabled="savingBill" @click="saveBill(false)">Save</button>
                        <button type="button" class="btn btn-primary" :disabled="savingBill" @click="saveBill(true)">Save & Print</button>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue';
import BillItemList from '@/components/molecule/company/hotel/hotel-checkin/BillItemList.vue';
import BillTotals from '@/components/molecule/company/hotel/hotel-checkin/BillTotals.vue';
import { inject, onMounted, provide, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import handlePurchase from '@/services/modules/purchase';
import handleInventory from '@/services/modules/inventory';
import handleCompany from '@/services/modules/company';
import hospitalPdfPrinterHelper from '@/services/utils/hospitalPdfPrinterHelper';

const props = defineProps({
    ipdRegisterId: {
        type: [Number, String],
        required: true,
    },
    guestId: {
        type: [Number, String],
        default: null,
    },
});

const $route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const {
    fetchProductList,
    fetchAccountReceivable,
    fetchVatPayable,
    getAccountHeadBySlag,
    storeBillPayment,
    fetchLatestSaleByIpd,
    fetchSalesBill,
    updateSalesBill,
} = handlePurchase();
const { getProduct } = handleInventory();
const { fetchCompanyInfo } = handleCompany();
const { generatePdf } = hospitalPdfPrinterHelper();

const savingBill = ref(false);
const existingBillId = ref(null);
const billProductId = ref(null);
const billItems = reactive([]);
const company = ref({});
const salesAccountHeads = ref([]);
const discountAccountHeads = ref([]);
const billFormData = ref({
    company_id: $route.params.companyId,
    contact_profile_id: props.guestId,
    account_head_id: null,
    ipd_register_id: props.ipdRegisterId,
    vat_payable_account_head_id: null,
    mop_account_head_id: null,
    receipt_reference: null,
    receipt_description: 'Hotel bill payment',
    payment: null,
    discount_head_id: null,
    adjustment_amount: 0,
    service_charge_head_id: null,
    service_charge_amount: 0,
    status: 'active',
    bill_number: '',
    date: '',
    has_item_detail: true,
    description: '',
    item_details: [],
});

provide('formData', billFormData);

watch(() => props.guestId, (guestId) => {
    billFormData.value.contact_profile_id = guestId;
});

const getBaseQuery = () => {
    return `?company_id=${$route.params.companyId}`;
};

const onDeleteBillItem = (index) => {
    billItems.splice(index, 1);
};

watch(billProductId, async () => {
    if (! billProductId.value) return;
    const found = billItems.find(item => item.product_id === billProductId.value);
    if (found) {
        showError('Item already selected!');
        billProductId.value = null;
        return;
    }
    const res = await getProduct({ id: billProductId.value, company_id: $route.params.companyId });
    billProductId.value = null;
    if (! res.data) {
        showError('Something went wrong please try again');
        return;
    }
    const product = res.data;
    billItems.push({
        product_id: product.id,
        name: product.name,
        description: product.description.unit.description,
        quantity: 1,
        rate: product.description.sales_price,
        discount: 0,
        vat: product.description.vat_rate,
        sub_total: 0,
        discount_amount: 0,
        discount_percent: 0,
        vat_amount: 0,
        total: 0,
        is_edit: false,
        product: { name: product.name },
    });
});

const loadBillAccountHeads = async () => {
    const companyQuery = getBaseQuery();
    await Promise.all([
        fetchAccountReceivable(companyQuery).then(res => {
            if (res.data) billFormData.value.account_head_id = res.data[0].id;
        }),
        fetchVatPayable(companyQuery).then(res => {
            if (res.data) billFormData.value.vat_payable_account_head_id = res.data[0].id;
        }),
        getAccountHeadBySlag('discount_account', companyQuery).then(res => {
            if (res.data) discountAccountHeads.value = res.data;
        }),
        getAccountHeadBySlag('sales_accounts', companyQuery).then(res => {
            if (res.data) salesAccountHeads.value = res.data;
        }),
    ]);
};

const applyBillData = (bill) => {
    billFormData.value.company_id = bill.company_id;
    billFormData.value.contact_profile_id = bill.contact_profile_id;
    billFormData.value.account_head_id = bill.account_head_id;
    billFormData.value.ipd_register_id = bill.ipd_register_id;
    billFormData.value.vat_payable_account_head_id = bill.vat_payable_account_head_id;
    billFormData.value.discount_head_id = bill.discount_head_id;
    billFormData.value.adjustment_amount = bill.round_up_discount;
    billFormData.value.service_charge_head_id = bill.service_charge_head_id;
    billFormData.value.service_charge_amount = bill.service_charge_amount;
    billFormData.value.status = 'active';
    billFormData.value.bill_number = bill.bill_number;
    billFormData.value.date = bill.date;
    billFormData.value.has_item_detail = bill.has_item_detail;
    billFormData.value.description = bill.description;
    billFormData.value.item_details = bill.general;

    billItems.splice(0, billItems.length);
    bill.general.forEach(({ product, ...item }) => {
        billItems.push({ ...item, name: product.name, product: { name: product.name } });
    });

    const receiptGeneral = bill.receipt_generals && bill.receipt_generals[0];
    if (! receiptGeneral) return;
    billFormData.value.mop_account_head_id = receiptGeneral.receipt_master.account_head_id;
    billFormData.value.receipt_master_id = receiptGeneral.receipt_master.id;
    billFormData.value.receipt_reference = receiptGeneral.receipt_master.receipt_reference;
    billFormData.value.payment = receiptGeneral.receipt_master.total_paid_amount;
};

const loadBill = async () => {
    await fetchLatestSaleByIpd(props.ipdRegisterId, getBaseQuery()).then(async (res) => {
        if (! res.status) return;
        existingBillId.value = res.data.id;
        await fetchSalesBill(res.data.id, getBaseQuery()).then(billRes => {
            if (billRes.data) applyBillData(billRes.data);
        });
    });
};

const validateBill = () => {
    if (! billItems.length) {
        showError('Please add at least one item to the bill');
        return false;
    }
    return true;
};

const submitBill = (payload) => {
    if (existingBillId.value) return updateSalesBill(existingBillId.value, payload);
    return storeBillPayment(payload);
};

const goToPrint = (id) => {
    fetchSalesBill(id, getBaseQuery()).then((res) => {
        if (! res.data) return;
        generatePdf(company.value, res.data, 'Hotel Bill', false, true);
    });
};

const saveBill = async (print) => {
    if (! validateBill()) return;
    billFormData.value.date = billFormData.value.date ? billFormData.value.date : new Date().toISOString().split('T')[0];
    billFormData.value.item_details = billItems;
    const payload = JSON.parse(JSON.stringify(billFormData.value));
    payload.item_details = billFormData.value.item_details;
    payload.sale_type = 'ipd_invoice';
    payload.receipt_type = 'ipd_invoice';

    savingBill.value = true;
    await submitBill(payload).then(async (res) => {
        if (! res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        if (print === true) goToPrint(res.data.id);
        await loadBill();
    }).finally(() => {
        savingBill.value = false;
    });
};

const loadCompany = async () => {
    await fetchCompanyInfo($route.params.companyId).then(res => {
        if (res.data) company.value = res.data;
    });
};

onMounted(async () => {
    await Promise.all([
        loadBillAccountHeads(),
        loadBill(),
        loadCompany(),
    ]);
});
</script>
